import { testimonials } from "../../utils/data";
import React, { useEffect, useRef, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "../Avatar/Avatar";
import { gsap } from "gsap";
import { useTranslation } from "react-i18next";

const Testimonials = () => {
  const [slice, setSlice] = useState(4);
  const [currentIndex, setCurrentIndex] = useState(0);
  const contentRefs = useRef([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const { t } = useTranslation("home");

  useEffect(() => {
    const handleResize = () => {
      const sliceSize =
        window.innerWidth < 768 ? 4 : window.innerWidth < 1024 ? 6 : 12;
      setSlice(sliceSize);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const animateContent = () => {
    if (isAnimating) return;

    setIsAnimating(true);

    // First fade out
    gsap.to(contentRefs.current, {
      opacity: 0,
      duration: 0.5,
      onComplete: () => {
        // Update state
        setCurrentIndex(
          (prev) => (prev + 1) % Math.ceil(t("testimonials.cards", { returnObjects: true }).length / slice)
        );

        // Reset opacity to 0 for new content
        gsap.set(contentRefs.current, { opacity: 0 });

        // Use a timeout to ensure state has updated before fading in
        setTimeout(() => {
          gsap.to(contentRefs.current, {
            opacity: 1,
            duration: 0.5,
            onComplete: () => {
              setIsAnimating(false);
            },
          });
        }, 50);
      },
    });
  };

  useEffect(() => {
    const interval = setInterval(animateContent, 4000);
    return () => clearInterval(interval);
  }, [slice, isAnimating]);

  const startIndex = currentIndex * slice;
  const currentTestimonials = t("testimonials.cards", { returnObjects: true }).slice(
    startIndex,
    startIndex + slice
  );

  return (
    <section className="flex flex-col justify-center items-center pt-16 sm-max:pt-10 sm-max:px-6 sm-max:py-10 sm:md-max:px-16 gap-9 px-32" id="review">
      <div className="flex flex-col items-center gap-4">
        <div className="px-3.5 py-2 shadow-blue rounded-full w-max bg-gradient-to-t from-[#3592FC] to-[#1F73F1] text-transparent bg-clip-text">
          {t("testimonials.miniTitle")}
        </div>
        <h2 className="text-[3.25rem] font-medium leading-[3.9rem] sm-max:text-[2.25rem] sm-max:leading-[2.875rem] sm-max:text-center">{t("testimonials.title")}</h2>
        <p className="mt-1 sm-max:text-center">
         {t("testimonials.description")}
        </p>
      </div>

      <div className="columns-4 sm-max:columns-1 sm:md-max:columns-2 lg:lg-small:columns-3 items-start relative sm-max:px-5">
        <div className="absolute bottom-0 w-full bg-gradient-to-t from-white to-transparent h-[100px] sm-max:hidden"></div>
        <div className="flex flex-col">
          {currentTestimonials.map((testimony, idx) => (
            <div
              key={testimony?.id}
              className="p-4 break-inside-avoid border border-blue-500 rounded-xl flex justify-start items-center flex-col gap-4 h-max sm-max:h-[180px] w-full mb-5"
            >
              <div
                ref={(el) => {
                  if (contentRefs.current) {
                    contentRefs.current[idx] = el;
                  }
                }}
                className="w-full opacity-0"
              >
                <div className="flex gap-2.5 items-center w-full mb-4">
                  <Avatar>
                    <AvatarImage src={`${testimony.avatar}`} alt="Avatar" />
                    <AvatarFallback className="font-medium text-white bg-[#72BDFF]">
                      {testimony.name[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex flex-col gap-0.5">
                    <p className="font-bold text-sm text-[#1A428E]">{testimony.name}</p>
                    <p className="text-xs text-[#1A428E]">{testimony.title}</p>
                  </div>
                </div>
                <p className="text-sm line-clamp-4 text-[#1A428E]">{testimony?.quote}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
